import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading, KPI, EmptyState } from "../components/UI";
import { Wallet, Plus, X, Download, CheckCircle2 } from "lucide-react";

export default function Refunds() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    const [r, t, u, l] = await Promise.all([
      api.get("/refunds"),
      api.get("/tenants").catch(()=>({data:[]})),
      api.get("/units").catch(()=>({data:[]})),
      api.get("/leases").catch(()=>({data:[]})),
    ]);
    setItems(r.data); setTenants(t.data); setUnits(u.data); setLeases(l.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post("/refunds", {
        tenant_id: f.get("tenant_id"),
        unit_id: f.get("unit_id") || null,
        lease_id: f.get("lease_id") || null,
        original_caution_fee: Number(f.get("original")),
        approved_deductions: Number(f.get("deductions") || 0),
        deduction_reasons: f.get("reasons") || "",
        refund_payment_date: f.get("refund_date") || null,
      });
      toast.success("Refund record created");
      setCreating(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const approve = async (id) => {
    try {
      await api.patch(`/refunds/${id}`, { refund_status: "approved" });
      toast.success("Refund approved");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const markPaid = async (id) => {
    try {
      await api.patch(`/refunds/${id}`, { refund_status: "paid", refund_payment_date: new Date().toISOString() });
      toast.success("Marked as paid");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const totalLiability = items.filter(r => r.refund_status !== "paid").reduce((s, r) => s + (r.refund_amount || 0), 0);
  const paidOut = items.filter(r => r.refund_status === "paid").reduce((s, r) => s + (r.refund_amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Caution Fee Refunds</h1>
          <p className="text-[#5E7166] text-sm">Track caution fees and refunds at move-out.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-gold" data-testid="new-refund-btn"><Plus size={16}/> New Refund</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KPI label="Outstanding Liability" value={naira(totalLiability)} icon={Wallet} accent/>
        <KPI label="Refunds Paid Out" value={naira(paidOut)} icon={CheckCircle2}/>
        <KPI label="Records" value={items.length} icon={Wallet}/>
      </div>

      <Card className="!p-0 overflow-hidden">
        {items.length === 0 ? <EmptyState icon={Wallet} title="No refunds yet" description="Create a refund record when a tenant moves out."/> :
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr><th>Tenant</th><th>Unit</th><th>Original</th><th>Deductions</th><th>Refund</th><th>Status</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.id} data-testid={`refund-${r.id}`}>
                    <td>{r.tenant?.full_name || "—"}</td>
                    <td>{r.unit?.unit_name || "—"}</td>
                    <td>{naira(r.original_caution_fee)}</td>
                    <td>{naira(r.approved_deductions)}<div className="text-xs text-[#5E7166] line-clamp-1">{r.deduction_reasons}</div></td>
                    <td className="font-serif font-bold text-[#0E5C3A]">{naira(r.refund_amount)}</td>
                    <td><span className={statusBadgeClass(r.refund_status)}>{statusLabel(r.refund_status)}</span></td>
                    <td className="text-xs">{formatDate(r.refund_payment_date) || "—"}</td>
                    <td className="flex gap-1">
                      {r.refund_status === "pending" && <button onClick={() => approve(r.id)} className="btn-outline-emerald text-xs">Approve</button>}
                      {r.refund_status === "approved" && <button onClick={() => markPaid(r.id)} className="btn-gold text-xs">Mark Paid</button>}
                      <a href={`${API_BASE}/refunds/${r.id}/pdf`} target="_blank" rel="noreferrer" className="btn-outline-emerald text-xs"><Download size={12}/></a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </Card>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-lg" data-testid="refund-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Create Refund Record</h3>
              <button onClick={() => setCreating(false)}><X size={20}/></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Tenant</span>
                <select name="tenant_id" required className="input-modav mt-1">
                  <option value="">—</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Unit (optional)</span>
                  <select name="unit_id" className="input-modav mt-1">
                    <option value="">—</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.unit_name}</option>)}
                  </select></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Lease (optional)</span>
                  <select name="lease_id" className="input-modav mt-1">
                    <option value="">—</option>
                    {leases.map(l => <option key={l.id} value={l.id}>{l.id.slice(0,8)} • {l.tenant?.full_name}</option>)}
                  </select></label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Original Caution Fee</span>
                  <input type="number" name="original" required className="input-modav mt-1"/></label>
                <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Approved Deductions</span>
                  <input type="number" name="deductions" defaultValue={0} className="input-modav mt-1"/></label>
              </div>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Reasons for Deductions</span>
                <textarea name="reasons" rows={3} className="input-modav mt-1" placeholder="Wall paint, broken bulbs, etc."/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Refund Payment Date (if known)</span>
                <input type="date" name="refund_date" className="input-modav mt-1"/></label>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="save-refund-btn">Save</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
